/**
 * TurfPulse Console — 24/7 iPad dashboard
 * ========================================
 *
 * Route: /console (admin-only)
 *
 * Design intent: a dedicated iPad on the wall, always on, never interacted with.
 * Must read well from 6 feet away. Dark background, oversized numerals,
 * muted greens/ambers so it's calm at night but still readable.
 *
 * Polls /api/admin/funnel/summary every 30 seconds and redraws.
 * All data shown is non-PII (emails are masked server-side).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const REFRESH_MS = 30_000;

// ─── Formatters ─────────────────────────────────────────────
const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');
const fmtPct = (n) => (typeof n === 'number' ? `${n.toFixed(1)}%` : '—');

function relTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const s = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

// ─── Sparkline (SVG, no deps) ────────────────────────────────
function Sparkline({ series, color = '#7dd3a1', label }) {
  const values = (series || []).map((r) => r.count || 0);
  const w = 320;
  const h = 70;
  if (values.length === 0) {
    return (
      <div className="text-neutral-500 text-sm" data-testid={`sparkline-empty-${label}`}>
        no activity in last 30 days
      </div>
    );
  }
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const path = values
    .map((v, i) => {
      const x = i * step;
      const y = h - (v / max) * (h - 6) - 3;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const area = `${path} L${w.toFixed(1)},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" data-testid={`sparkline-${label}`}>
      <path d={area} fill={color} fillOpacity="0.15" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  );
}

// ─── KPI tile ────────────────────────────────────────────────
function KpiTile({ label, value, sub, accent = 'text-emerald-400', testid }) {
  return (
    <div
      className="bg-neutral-900/70 border border-neutral-800 rounded-2xl px-6 py-5 backdrop-blur"
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
        {label}
      </div>
      <div className={`text-5xl font-semibold tabular-nums ${accent}`}>{value}</div>
      {sub ? (
        <div className="text-sm text-neutral-400 mt-2">{sub}</div>
      ) : null}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────
export default function ConsolePage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [clockTick, setClockTick] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/funnel/summary`, {
        withCredentials: true,
      });
      setData(res.data);
      setErr(null);
      setLastRefresh(new Date());
    } catch (e) {
      setErr(e?.response?.status === 401
        ? 'Not signed in as admin. Please sign in from the homepage (turfpulse.app → Sign In) and return to /console.'
        : 'Could not reach the funnel API — retrying…');
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // Ticking clock for relative-time labels (every 10s)
  useEffect(() => {
    const id = setInterval(() => setClockTick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  // Prevent screen dim on iPad via Wake Lock when available
  useEffect(() => {
    let wakeLock = null;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch {
        /* ignore — best-effort only */
      }
    };
    acquire();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (wakeLock) wakeLock.release?.().catch(() => {});
    };
  }, []);

  const w7 = data?.windows?.['7d'];
  const w30 = data?.windows?.['30d'];
  const wAll = data?.windows?.all_time;

  const pulse = useMemo(() => {
    // green if any tap/lead in last 15 min, amber in last 2h, grey else
    if (!data?.latest_event?.at) return { tone: 'grey', label: 'quiet' };
    const ageSec = (Date.now() - new Date(data.latest_event.at).getTime()) / 1000;
    if (ageSec < 900) return { tone: 'green', label: 'live' };
    if (ageSec < 7200) return { tone: 'amber', label: 'warm' };
    return { tone: 'grey', label: 'quiet' };
  }, [data, clockTick]);

  const pulseColor = {
    green: 'bg-emerald-400',
    amber: 'bg-amber-400',
    grey: 'bg-neutral-500',
  }[pulse.tone];

  return (
    <div
      className="min-h-screen bg-neutral-950 text-neutral-100 px-8 py-6 font-mono"
      data-testid="console-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <span
              className={`inline-block w-3 h-3 rounded-full ${pulseColor} ${pulse.tone === 'green' ? 'animate-pulse' : ''}`}
              data-testid="console-pulse-dot"
            />
            <h1 className="text-3xl font-semibold tracking-tight">
              TurfPulse Console
            </h1>
            <span className="text-sm text-neutral-500 uppercase tracking-widest ml-2">
              {pulse.label}
            </span>
          </div>
          <div className="text-sm text-neutral-500 mt-1">
            live funnel · auto-refresh 30s ·{' '}
            {lastRefresh ? `updated ${relTime(lastRefresh.toISOString())}` : 'loading…'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl tabular-nums text-neutral-200">
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-xs text-neutral-500 uppercase tracking-widest">
            {new Date().toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
        </div>
      </div>

      {err ? (
        <div
          className="bg-rose-950/60 border border-rose-900 text-rose-200 rounded-xl px-6 py-4 mb-6"
          data-testid="console-error"
        >
          {err}
        </div>
      ) : null}

      {/* Top KPIs — last 7 days */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiTile
          label="NFC Taps · 7d"
          value={fmtInt(w7?.taps)}
          sub={`${fmtInt(w30?.taps)} in 30d · ${fmtInt(wAll?.taps)} all-time`}
          accent="text-emerald-400"
          testid="kpi-taps"
        />
        <KpiTile
          label="Leads · 7d"
          value={fmtInt(w7?.leads)}
          sub={`${fmtInt(w30?.leads)} in 30d · ${fmtInt(wAll?.leads)} all-time`}
          accent="text-sky-400"
          testid="kpi-leads"
        />
        <KpiTile
          label="Signups · 7d"
          value={fmtInt(w7?.signups)}
          sub={`${fmtInt(w30?.signups)} in 30d`}
          accent="text-violet-400"
          testid="kpi-signups"
        />
        <KpiTile
          label="Affiliate clicks · 7d"
          value={fmtInt(w7?.affiliate_clicks)}
          sub={`${fmtInt(w30?.affiliate_clicks)} in 30d · ${fmtInt(wAll?.affiliate_clicks)} all-time`}
          accent="text-amber-400"
          testid="kpi-affiliate"
        />
      </div>

      {/* Conversion row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <KpiTile
          label="Tap → Lead · 30d"
          value={fmtPct(w30?.conversion?.tap_to_lead)}
          sub="cards that caused an email opt-in"
          accent="text-teal-300"
          testid="kpi-conv-tap-lead"
        />
        <KpiTile
          label="Lead → Signup · 30d"
          value={fmtPct(w30?.conversion?.lead_to_signup)}
          sub="emails that created real accounts"
          accent="text-teal-300"
          testid="kpi-conv-lead-signup"
        />
        <KpiTile
          label="Tap → Signup · 30d"
          value={fmtPct(w30?.conversion?.tap_to_signup)}
          sub="full-funnel yield per physical tap"
          accent="text-teal-300"
          testid="kpi-conv-tap-signup"
        />
      </div>

      {/* Sparklines */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
            Taps by day · 30d
          </div>
          <Sparkline series={data?.taps_by_day_30d} color="#34d399" label="taps" />
        </div>
        <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
            Leads by day · 30d
          </div>
          <Sparkline series={data?.leads_by_day_30d} color="#38bdf8" label="leads" />
        </div>
      </div>

      {/* Bottom row: top campaigns + recent signups */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
            Top campaigns · 30d
          </div>
          {(data?.top_campaigns_30d || []).length === 0 ? (
            <div className="text-neutral-500 text-sm">no taps yet</div>
          ) : (
            <ul className="space-y-2" data-testid="top-campaigns-list">
              {data.top_campaigns_30d.map((c, i) => (
                <li
                  key={`${c.campaign || 'default'}-${c.card_slug || i}`}
                  className="flex items-baseline justify-between"
                >
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-neutral-400 tabular-nums text-sm w-5">
                      {i + 1}.
                    </span>
                    <span className="text-neutral-100 truncate">
                      {c.card_slug || c.campaign || 'default'}
                    </span>
                    {c.card_slug && c.campaign && c.campaign !== c.card_slug ? (
                      <span className="text-neutral-500 text-xs">
                        · {c.campaign}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-baseline gap-3 text-sm">
                    <span className="text-emerald-400 tabular-nums font-semibold">
                      {fmtInt(c.count)}
                    </span>
                    <span className="text-neutral-500 w-20 text-right">
                      {relTime(c.last_tap_at)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
            Recent signups
          </div>
          {(data?.recent_signups || []).length === 0 ? (
            <div className="text-neutral-500 text-sm">no signups yet</div>
          ) : (
            <ul className="space-y-2" data-testid="recent-signups-list">
              {data.recent_signups.map((s, i) => (
                <li
                  key={`${s.email_masked}-${i}`}
                  className="flex items-baseline justify-between text-sm"
                >
                  <span className="text-neutral-200 truncate">
                    {s.name || s.email_masked}
                  </span>
                  <span className="text-neutral-500 w-20 text-right">
                    {relTime(s.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Top affiliate products */}
      <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5 mb-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
          Top affiliate clicks · 30d
        </div>
        {(data?.top_affiliate_products_30d || []).length === 0 ? (
          <div className="text-neutral-500 text-sm">no affiliate clicks yet</div>
        ) : (
          <ul className="space-y-2" data-testid="top-products-list">
            {data.top_affiliate_products_30d.map((p, i) => (
              <li
                key={`${p.product_key}-${p.network}`}
                className="flex items-baseline justify-between text-sm"
              >
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-neutral-400 tabular-nums w-5">
                    {i + 1}.
                  </span>
                  <span className="text-neutral-100 truncate">{p.product_key}</span>
                  <span className="text-neutral-500 text-xs uppercase">
                    · {p.network}
                  </span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-amber-400 tabular-nums font-semibold">
                    {fmtInt(p.clicks)}
                  </span>
                  <span className="text-neutral-500 text-xs">
                    {fmtInt(p.unique_clicks)} uniq
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Heartbeat ticker */}
      {data?.latest_event ? (
        <div
          className="text-xs text-neutral-500 tracking-wide"
          data-testid="console-heartbeat"
        >
          last event · {data.latest_event.label} · {relTime(data.latest_event.at)}
        </div>
      ) : null}
    </div>
  );
}
